import React, { useEffect } from "react";
import imgBlue from "../../assets/2.png";
import { useTranslation } from "react-i18next";
import { indexStore } from "../../store/indexStore";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetClose,
} from "../ui/sheet";
import { Menu, Home, Sparkles, Briefcase, DollarSign, Mail, X } from "lucide-react";
import { motion } from "framer-motion";

import Spacer from "../Spacer";
import i18n from "../../i18n";
import uaeFlag from "../../assets/uae-flag.png";
import usaFlag from "../../assets/usa-flag.png";

function HeaderPage() {
  const { t } = useTranslation();
  const { lang, changeLang } = indexStore();
  const linkClassName = "text-base md:text-lg capitalize font-medium transition-colors duration-300 hover:text-MainBlue relative group";

  useEffect(() => {
    const handleLanguageChange = () => {
      i18n.changeLanguage(lang);
      document.documentElement.dir = i18n.dir();
    };

    if (lang) {
      handleLanguageChange();
    } else {
      changeLang(localStorage.getItem("Lang") || "en");
    }
  }, [lang, changeLang]);

  const toggleLanguage = () => {
    changeLang(lang === "ar" ? "en" : "ar");
  };

  return (
    <div className="grid grid-cols-3 items-center shadow-sm px-4 relative bg-background">
      <a href="/" className="">
        <img width={150} height={100} src={imgBlue} alt="cl" className="p-2" />
      </a>

          <nav className="hidden md:flex gap-6 lg:gap-8 items-center justify-center">
            <a className={linkClassName} href="/">
              {t("pages.home")}
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-MainBlue transition-all duration-300 group-hover:w-full"></span>
            </a>
            <a className={linkClassName} href="/features">
              {t("pages.features")}
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-MainBlue transition-all duration-300 group-hover:w-full"></span>
            </a>
            <a className={linkClassName} href="/services">
              {t("pages.services")}
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-MainBlue transition-all duration-300 group-hover:w-full"></span>
            </a>
            <a className={linkClassName} href="/pricing">
              {t("pages.pricing")}
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-MainBlue transition-all duration-300 group-hover:w-full"></span>
            </a>
            <a className={linkClassName} href="/contact">
              {t("pages.contact")}
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-MainBlue transition-all duration-300 group-hover:w-full"></span>
            </a>
          </nav>

          <div className="hidden md:flex items-center justify-end gap-3">
            <LangButtonToggle language={lang} toggleLanguage={toggleLanguage} />
          </div>

          <Spacer className="md:hidden" flex={true} />

          <div className="md:hidden flex items-center gap-3 justify-end">
            <LangButtonToggle language={lang} toggleLanguage={toggleLanguage} />

            <Sheet>
              <SheetTrigger asChild>
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  className="p-2 rounded-lg hover:bg-muted transition-colors"
                >
                  <Menu className="w-6 h-6" />
                </motion.button>
              </SheetTrigger>

              <SheetContent 
              hiddenCloseButton={true}
                side={`${lang === "ar" ? "left" : "right"}`} 
                className="w-[320px] sm:w-[380px] p-0 bg-gradient-to-br from-background via-background to-muted/30"
              >
                {/* Header with gradient */}
                <div className="relative bg-gradient-to-r from-MainBlue/10 via-blue-600/5 to-MainBlue/10 p-6 border-b border-border/50">
                  <div className="absolute inset-0 bg-gradient-to-r from-MainBlue/5 to-blue-600/5"></div>
                  <div className="relative z-10 flex items-center justify-between">
                    <h2 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-MainBlue to-blue-600 bg-clip-text text-transparent">
                      {t("pages.menu")}
                    </h2>
                    <SheetClose asChild>
                      <motion.button
                        whileHover={{ scale: 1.1, rotate: 90 }}
                        whileTap={{ scale: 0.9 }}
                        className="p-2 rounded-lg hover:bg-MainBlue/10 transition-colors"
                      >
                        <X className="w-5 h-5 text-foreground" />
                      </motion.button>
                    </SheetClose>
                  </div>
                </div>

                {/* Navigation Links */}
                <nav className="flex flex-col p-4 gap-2">
                  {[
                    { href: "/", icon: Home, label: t("pages.home") },
                    { href: "/features", icon: Sparkles, label: t("pages.features") },
                    { href: "/services", icon: Briefcase, label: t("pages.services") },
                    { href: "/pricing", icon: DollarSign, label: t("pages.pricing") },
                    { href: "/contact", icon: Mail, label: t("pages.contact") },
                  ].map((item, index) => (
                    <motion.a
                      key={item.href}
                      initial={{ opacity: 0, x: lang === "ar" ? 20 : -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.1 }}
                      whileHover={{ x: lang === "ar" ? -8 : 8, scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      href={item.href}
                      className="group flex items-center gap-4 p-4 rounded-xl bg-card hover:bg-gradient-to-r hover:from-MainBlue/10 hover:to-blue-600/10 border-2 border-transparent hover:border-MainBlue/30 transition-all duration-300 shadow-sm hover:shadow-md"
                    >
                      <div className="flex-shrink-0 p-2.5 rounded-lg bg-MainBlue/10 group-hover:bg-MainBlue/20 transition-colors">
                        <item.icon className="w-5 h-5 text-MainBlue" />
                      </div>
                      <span className="flex-1 text-base md:text-lg font-semibold text-foreground group-hover:text-MainBlue transition-colors">
                        {item.label}
                      </span>
                      <motion.div
                        initial={{ opacity: 0, x: lang === "ar" ? 10 : -10 }}
                        whileHover={{ opacity: 1, x: 0 }}
                        className="text-MainBlue"
                      >
                        <svg
                          className={`w-5 h-5 ${lang === "ar" ? "rotate-180" : ""}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      </motion.div>
                    </motion.a>
                  ))}
                </nav>

                {/* Footer Section */}
                <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-border/50 bg-muted/30">
                  <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                    <span>© {new Date().getFullYear()} TWH Technologies</span>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
  );
}

export default HeaderPage;
const LangButtonToggle = ({
  language,
  toggleLanguage,
}: {
  language: string;
  toggleLanguage: () => void;
}) => (
  <motion.button
    onClick={toggleLanguage}
    whileHover={{ scale: 1.05 }}
    whileTap={{ scale: 0.95 }}
    className="flex items-center gap-2 rounded-lg p-2 md:p-2.5 bg-muted hover:bg-MainBlue/10 border border-border hover:border-MainBlue transition-all duration-300 group"
  >
    {language === "ar" ? (
      <>
        <img src={uaeFlag} alt="ع" className="w-5 h-5 md:w-6 md:h-6 rounded-sm" />
        <span className="font-semibold text-sm md:text-base group-hover:text-MainBlue transition-colors">ع</span>
      </>
    ) : (
      <>
        <img src={usaFlag} alt="EN" className="w-5 h-5 md:w-6 md:h-6 rounded-sm" />
        <span className="font-semibold text-sm md:text-base group-hover:text-MainBlue transition-colors">EN</span>
      </>
    )}
  </motion.button>
);
